import React, { useState, useEffect, useRef, useCallback } from "react";
import "./YearlyHoroscope.css";
import APIURL from "../../common/Config";
import { useNavigate, useParams, Link } from "react-router-dom";
import Header from "../header/header";
import Footer from "../footer/footer";

const YearlyHoroscope = () => {
  const navigate = useNavigate();
  const { sign: paramSign = "aries" } = useParams();

  const formatSignName = (str) =>
    str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  const [selectedSign, setSelectedSign] = useState(formatSignName(paramSign));
  const [horoscopeData, setHoroscopeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeIndex, setActiveIndex] = useState(null); // FAQ toggle
  const inMemoryCacheRef = useRef(new Map());

  const staticMeta = {
    title: "AstroBhagya – Free Yearly Horoscope for All 12 Zodiac Signs",
    description:
      "Free yearly horoscope predictions 2025 for Aries to Pisces. Read 12 zodiac sign astrology prediction and yearly horoscope 2025 for love, career, and health.",
    keywords:
      "Free yearly horoscope predictions 2025, 12 zodiac sign astrology prediction, yearly Horoscope 2025 for all signs",
    author: "Heavenly Expectation Private Limited",
    revisitAfter: "7 days",
    distribution: "global",
    rating: "general",
  };

  const signImages = {
    Aries: require("../../assets/Ariesss.webp"),
    Taurus: require("../../assets/Taurusss.webp"),
    Gemini: require("../../assets/Geminiii.webp"),
    Cancer: require("../../assets/Cancerrr.webp"),
    Leo: require("../../assets/Leooo.webp"),
    Virgo: require("../../assets/Virgooo.webp"),
    Libra: require("../../assets/Libraaa.webp"),
    Scorpio: require("../../assets/Scorpiooo.webp"),
    Sagittarius: require("../../assets/Sagittariusss.webp"),
    Capricorn: require("../../assets/Capricornnn.webp"),
    Aquarius: require("../../assets/Aqauriusss.webp"),
    Pisces: require("../../assets/Piscesss.webp"),
  };

  // YEARLY API CALL (20250701 data exists!)
  const fetchYearlyCards = useCallback(
    async (retries = 3) => {
      setError(null);
      setLoading(true);
      const dateStr = "20260101";
      const cacheKey = `yearlycards_${dateStr}`;

      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        try {
          setHoroscopeData(JSON.parse(cached));
          setLoading(false);
          return;
        } catch {}
      }

      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);

        const response = await fetch(
          `${APIURL}horroscope/getallprediction?type=yearly&startDate=${dateStr}&endDate=${dateStr}&language=english`,
          { signal: controller.signal }
        );
        clearTimeout(timeoutId);

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const data = await response.json();

        if (Array.isArray(data)) {
          const signMap = {
            ARIES: "Aries",
            TAURUS: "Taurus",
            GEMINI: "Gemini",
            CANCER: "Cancer",
            LEO: "Leo",
            VIRGO: "Virgo",
            LIBRA: "Libra",
            SCORPIO: "Scorpio",
            SAGITTARIUS: "Sagittarius",
            CAPRICORN: "Capricorn",
            AQUARIUS: "Aquarius",
            PISCES: "Pisces",
          };

          const groupedData = data.map((item) => {
            const sign = signMap[item.sun_shine?.toUpperCase()] || "Aries";
            const cleanText =
              item.prediction
                ?.replace(/<[^>]*>/g, "")
                ?.replace(/\n+/g, " ")
                ?.trim()
                ?.substring(0, 120) + "..." || "Discover 2025 predictions";

            return { ...item, displaySign: sign, preview: cleanText };
          });

          localStorage.setItem(cacheKey, JSON.stringify(groupedData));
          setHoroscopeData(groupedData);
        }
      } catch (err) {
        console.error("API Error:", err);
        setError("Using cached content");
        // Static fallback
        setHoroscopeData([
          { displaySign: "Aries", preview: "As we step into 2026, the year brings a chance...", id: 1 },
          {
            displaySign: "Taurus",
            preview: "As 2026 begins, Taurus, the year invites you to...",
            id: 2,
          },
          {
            displaySign: "Gemini",
            preview: "As 2026 unfolds, Gemini, the year asks you to...",
            id: 3,
          },
          {
            displaySign: "Cancer",
            preview: "2026 will be a year where life asks you to...",
            id: 4,
          },
          { displaySign: "Leo", preview: "2026 is set to be a year where your practicality meets...", id: 5 },
          {
            displaySign: "Virgo",
            preview: "Practical progress Virgo...",
            id: 6,
          },
          { displaySign: "Libra", preview: "2026 brings Libra a year of transformation through...", id: 7 },
          {
            displaySign: "Scorpio",
            preview: "2026 is a year of growth and renewal for Scorpio, bringing...",
            id: 8,
          },
          {
            displaySign: "Sagittarius",
            preview: "In 2026, Sagittarius, the year is marked by opportunities to...",
            id: 9,
          },
          {
            displaySign: "Capricorn",
            preview: "In 2026, Capricorn, the year encourages you to combine...",
            id: 10,
          },
          {
            displaySign: "Aquarius",
            preview: "In 2026, Aquarius, the year encourages you to embrace your...",
            id: 11,
          },
          {
            displaySign: "Pisces",
            preview: "In 2026, Pisces, the stars encourage you to explore your inner world...",
            id: 12,
          },
        ]);
      } finally {
        setLoading(false);
      }
    },
    [APIURL]
  );

  useEffect(() => {
    fetchYearlyCards();

    // Meta tags
    document.title = staticMeta.title;
    const setMeta = (name, content) => {
      let meta = document.querySelector(`meta[name="${name}"]`);
      if (!meta) {
        meta = document.createElement("meta");
        meta.name = name;
        document.head.appendChild(meta);
      }
      meta.content = content;
    };
    Object.entries(staticMeta).forEach(([key, value]) => setMeta(key, value));
  }, []);

  const toggleAnswer = (index) => {
    setActiveIndex((prev) => (prev === index ? null : index));
  };

  if (loading)
    return (
      <div style={{ padding: "100px", textAlign: "center" }}>
        <div
          style={{
            width: "60px",
            height: "60px",
            border: "5px solid #f3f3f3",
            borderTop: "5px solid #ff6b35",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
            margin: "0 auto 30px",
          }}
        />
        <p style={{ fontSize: "18px" }}>Loading 2025 Horoscopes...</p>
      </div>
    );

  return (
    <div>
      <Header />
      <div className="yearly-wrapper">
        {/* ORIGINAL LONG INTRO TEXT */}
        <h1 className="main-title">
          Your Yearly Horoscope for 2026: Guidance for the Year Ahead
        </h1>

        <p className="intro-text">
          As we step into 2026, the year brings a chance to slow down, think
          clearly, and move ahead with confidence. This year is not about
          rushing or making quick decisions. Instead, it encourages you to
          understand yourself better and choose what truly matters in your life.
          2026 is about steady growth, balance, and making thoughtful choices.
          <br />
          <br />
          Your Yearly Horoscope 2026 is created to help you understand what the
          coming year may bring and how you can prepare for it. It does not
          promise exact events, but it helps you recognise important phases and
          opportunities. Whether you are focusing on your career, relationships,
          health, or personal happiness, astrology can guide you in the right
          direction
          <br />
          <br />
          In 2026, the planetary movements support discipline and long-term
          planning. Some periods may ask for patience, while others may open
          doors for progress and learning. Jupiter encourages growth and
          positivity, especially where you put in honest effort. Saturn reminds
          you to stay responsible and committed to your goals. Eclipses during
          the year may bring sudden clarity, helping you see situations more
          clearly and make better decisions.
          <br />
          <br />
          Each zodiac sign will experience 2026 differently. For some, it may be
          a year to build a strong career foundation. For others, it could be
          about improving relationships, healing emotionally, or discovering new
          interests. Challenges may come, but they will help you become stronger
          and wiser. The key lesson of 2026 is to move forward step by step and
          trust the process.
          <br />
          <br />
          Astrology is not about fear or fixed destiny. It is about awareness
          and preparation. Your horoscope helps you understand the right time to
          act, the right time to wait, and the right time to reflect. The final
          choices always remain in your hands.
          <br />
          <br />
          With the AstroBhagya app, these astrological insights become easy to
          access anytime. The app offers personalised horoscopes, regular
          updates, and simple guidance to help you stay aware throughout the
          year. AstroBhagya connects ancient astrology with modern life, making
          it easy for you to use astrology as a daily support.
          <br />
          <br />
          Let your 2026 yearly horoscope guide you toward a calmer, clearer, and
          more confident year ahead. With AstroBhagya by your side, move into
          2026 feeling prepared, balanced, and ready to grow.
        </p>

        {/* DYNAMIC CARDS */}
        <div className="horoscopes-cards">
          {horoscopeData.map((item, index) => (
            <div key={item.id || index} className="horoscopes-card">
              <div className="horoscopes-card-header">
                <img
                  src={signImages[item.displaySign]}
                  alt={item.displaySign}
                  className="horoscopes-icon"
                />
                <h3>{item.displaySign} Yearly Horoscope</h3>
              </div>
              <p>{item.preview}</p>
              <Link
                to={`/yearly-horoscope/${item.displaySign.toLowerCase()}`}
                className="read-more-btn"
              >
                Read More
              </Link>
            </div>
          ))}
        </div>

        {/* DAILY EXACT FAQ STYLE */}
        <div className="faq-section">
          <div className="container-item">
            <h3>Frequently Asked Questions (FAQs) – Yearly Horoscope</h3>
            <div className="fitem">
              {[
                {
                  question: "1. What is a yearly horoscope?",
                  answer:
                    "A yearly horoscope provides astrological predictions for the entire year based on your zodiac sign, covering love, career, health, and personal growth.",
                },
                {
                  question: "2. How accurate are yearly horoscopes?",
                  answer:
                    "Yearly horoscopes offer guidance based on major planetary movements and transits throughout the year.",
                },
                {
                  question: "3. Can a yearly horoscope predict my future?",
                  answer:
                    "Yearly horoscopes provide insights into potential trends and energies. They guide you through opportunities but don't dictate exact events.",
                },
                {
                  question:
                    "4. Why do different astrologers give different yearly predictions?",
                  answer:
                    "Astrologers use different calculation methods and interpretations of planetary influences.",
                },
                {
                  question: "5. Should I read my yearly horoscope every month?",
                  answer:
                    "Review your yearly horoscope monthly to stay aligned with the year's major themes and transits.",
                },
                {
                  question: "6. How is my yearly horoscope calculated?",
                  answer:
                    "Based on your sun sign and major planetary transits (Jupiter, Saturn, Rahu-Ketu) throughout the year.",
                },
                {
                  question:
                    "7. When should I follow yearly horoscope remedies?",
                  answer:
                    "Follow remedies during key planetary transits mentioned in your yearly predictions.",
                },
                {
                  question: "8. Can I read yearly horoscope for moon sign?",
                  answer:
                    "Yearly horoscopes work best with sun sign. Moon sign readings require personalized analysis.",
                },
                {
                  question: "9. How do yearly horoscopes help decision-making?",
                  answer:
                    "They highlight favorable periods for major decisions in career, relationships, and investments.",
                },
                {
                  question:
                    "10. Where can I get personalized yearly horoscope?",
                  answer:
                    "Consult AstroBhagya's expert astrologers for your complete birth chart analysis.",
                },
              ].map((item, index) => (
                <div className="faq-item" key={index}>
                  <div
                    className={`faq-question ${
                      activeIndex === index ? "active" : ""
                    }`}
                    onClick={() => toggleAnswer(index)}
                  >
                    {item.question}
                  </div>
                  <div
                    className={`faq-answer ${
                      activeIndex === index ? "show" : ""
                    }`}
                  >
                    {item.answer}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default YearlyHoroscope;

